import { type AcceptState } from '~/modules/delivery-notes/list/constants';

import { type AcceptRole, type DeliveryNoteAssetMainOrChain } from '../types';

/**
 * Maps a boolean accept status to an AcceptState string.
 *
 * @param acceptStatus - The boolean status indicating acceptance (true), rejection (false), or undefined (open).
 * @returns The corresponding AcceptState: 'accepted', 'rejected', or 'open'.
 */
export const mapAcceptStatusToAcceptState = (
  acceptStatus: boolean | undefined,
): AcceptState => {
  if (acceptStatus === true) {
    return 'accepted';
  }

  if (acceptStatus === false) {
    return 'rejected';
  }

  return 'open';
};

/**
 * Extracts the accept states for each party from a delivery note object.
 *
 * @param deliveryNote - The delivery note object containing acceptStates.
 * @returns An object mapping each AcceptRole to its AcceptState.
 */
export const getPartyAcceptStates = (
  deliveryNote: DeliveryNoteAssetMainOrChain,
): Record<AcceptRole, AcceptState> => {
  return {
    carrier: mapAcceptStatusToAcceptState(
      deliveryNote?.acceptStates?.acceptStatus?.carrier,
    ),
    onBehalfCarrier: mapAcceptStatusToAcceptState(
      deliveryNote?.acceptStates?.acceptStatus?.carrier,
    ),
    onBehalfRecipient: mapAcceptStatusToAcceptState(
      deliveryNote?.acceptStates?.acceptStatus?.recipient,
    ),
    onBehalfSupplier: mapAcceptStatusToAcceptState(
      deliveryNote?.acceptStates?.acceptStatus?.supplier,
    ),
    recipient: mapAcceptStatusToAcceptState(
      deliveryNote?.acceptStates?.acceptStatus?.recipient,
    ),
    supplier: mapAcceptStatusToAcceptState(
      deliveryNote?.acceptStates?.acceptStatus?.supplier,
    ),
  };
};

/**
 * Determines the overall accept state from the accept states of all parties.
 *
 * @param partyAcceptStates - An object mapping each AcceptRole to its AcceptState.
 * @returns The overall AcceptState:
 * - 'rejected' if any party rejected (including onBehalfRecipient),
 * - 'accepted' if recipient or onBehalfRecipient accepted (and none rejected),
 * - otherwise 'open'.
 */
export const getOverallAcceptState = (
  partyAcceptStates: Record<AcceptRole, AcceptState>,
): AcceptState => {
  if (Object.values(partyAcceptStates ?? {}).includes('rejected')) {
    // If any party has rejected, we consider the delivery note to be rejected.
    return 'rejected';
  }

  if (
    [
      partyAcceptStates?.recipient,
      partyAcceptStates?.onBehalfRecipient,
    ].includes('accepted')
  ) {
    // We know there are no rejections (see check above),
    // so if the recipient (or onBehalfRecipient) accepted, we consider the delivery note to be accepted.
    return 'accepted';
  }

  // We know no party has accepted or rejected (see checks above),
  // so all accept states should be empty and we consider the delivery note to be open.
  return 'open';
};

/**
 * Returns the accept states (i.e. if a delivery note has been signed) for all parties and the overall accept state for a delivery note.
 *
 * @param deliveryNote - The delivery note object containing acceptStates.
 * @returns An object with the overall accept state and each party's accept state.
 */
export const getDeliveryNoteAcceptState = (
  deliveryNote: DeliveryNoteAssetMainOrChain,
): Record<AcceptRole | 'overall', AcceptState> => {
  const partyAcceptStates = getPartyAcceptStates(deliveryNote);

  return {
    overall: getOverallAcceptState(partyAcceptStates),
    ...partyAcceptStates,
  };
};
